import { useState } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { Note } from '../components/Note';
import { StatStrip } from '../components/StatStrip';
import { useAuth } from '../auth';
import { formatDateTime } from '../format';
import { DISPLAY, MONO, tokens } from '../theme';

/**
 * Số liệu vận hành và theo dõi lỗi — spec SC-16 §4, §5.
 *
 * Trước màn này, cách duy nhất để biết hệ thống có khoẻ hay không là ssh vào
 * server và đọc `docker compose logs` — mà log đó mất mỗi lần container bị tạo lại.
 */

/**
 * Dung lượng ở đơn vị đọc được.
 *
 * Phải xuống tận KB: khi mới chạy, mọi bảng đều dưới 1 MB và làm tròn về "0 MB"
 * biến cả bảng số liệu thành một cột số không — đúng lúc người ta cần một mốc
 * ban đầu để về sau so xem nó phình bao nhiêu.
 */
function bytesText(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
}

/**
 * Biểu đồ cột thuần CSS cho số lượt quét 30 ngày.
 *
 * Không kéo thêm thư viện biểu đồ cho một dãy 30 số: bundle của giao diện đã hơn
 * 600 KB, và cái cần trả lời ở đây chỉ là "hôm nào quét nhiều, hôm nào đứng im".
 */
function ScanChart({ data }: { data: { ngay: string; soLuot: number }[] }) {
  if (data.length === 0) {
    return <Note>Chưa có lượt quét nào trong 30 ngày qua.</Note>;
  }
  const max = Math.max(...data.map((d) => d.soLuot));

  return (
    <Stack direction="row" spacing={0.4} alignItems="flex-end" sx={{ height: 120, mt: 1 }}>
      {data.map((d) => (
        <Box
          key={d.ngay}
          title={`${d.ngay}: ${d.soLuot} lượt`}
          sx={{
            flex: 1,
            minWidth: 4,
            // Tối thiểu 3px để ngày có đúng 1 lượt vẫn nhìn thấy được — cột cao 0px
            // đọc y hệt ngày không có lượt nào.
            height: `${Math.max(3, (d.soLuot / max) * 100)}%`,
            bgcolor: tokens.teal,
            borderRadius: '3px 3px 0 0',
          }}
        />
      ))}
    </Stack>
  );
}

function MetricsTab() {
  const { data, isLoading, error } = useQuery({ queryKey: ['ops-metrics'], queryFn: api.opsMetrics });

  if (isLoading) return <Skeleton variant="rounded" height={300} />;
  if (error) return <Note tone="bad">{(error as Error).message}</Note>;
  if (!data) return null;

  return (
    <Box>
      <StatStrip
        items={[
          { value: data.scanTotal.toLocaleString('vi-VN'), label: 'Tổng lượt quét' },
          {
            value: data.scanAvgMs === null ? '—' : `${data.scanAvgMs} ms`,
            label: 'Trang đích phản hồi (trung bình 30 ngày)',
          },
          {
            value: data.scanErrorCount.toLocaleString('vi-VN'),
            label: 'Lỗi ở đường quét',
            color: data.scanErrorCount ? tokens.roseText : tokens.okText,
          },
          { value: bytesText(data.dbSizeBytes), label: 'Dung lượng cơ sở dữ liệu' },
        ]}
      />

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 600, color: tokens.ink }}>
          Lượt quét 30 ngày qua
        </Typography>
        <ScanChart data={data.scansByDay} />
      </Box>

      <Box sx={{ mt: 3, borderTop: `1px solid ${tokens.line2}`, pt: 2 }}>
        <StatStrip
          items={[
            { value: String(data.pageCount), label: 'Trang đang theo dõi' },
            {
              value: String(data.pageNeverScanned),
              label: 'Chưa quét lần nào',
              color: data.pageNeverScanned ? tokens.amberText : undefined,
            },
            {
              value: String(data.pageRenderWarning),
              label: 'Nghi dựng bằng JavaScript',
              color: data.pageRenderWarning ? tokens.amberText : undefined,
            },
            { value: String(data.activeUsers7d), label: 'Người dùng hoạt động 7 ngày' },
          ]}
        />
      </Box>

      {data.biggestTables.length > 0 && (
        <Box sx={{ mt: 3, borderTop: `1px solid ${tokens.line2}`, pt: 2 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 600, color: tokens.ink }}>
            Bảng lớn nhất
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            Cảnh báo sớm cho vấn đề phình dữ liệu — bảng Scan lớn nhanh nhất vì mỗi lượt quét lưu
            cả ảnh chụp trang.
          </Typography>
          {data.biggestTables.map((t) => (
            <Stack key={t.ten} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>{t.ten}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 13, color: 'text.secondary' }}>
                {bytesText(t.bytes)}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  );
}

function ErrorsTab() {
  const [requestId, setRequestId] = useState('');
  const tim = requestId.trim();

  const groups = useQuery({
    queryKey: ['error-groups'],
    queryFn: api.errorGroups,
    enabled: tim.length === 0,
  });
  const byId = useQuery({
    queryKey: ['errors-by-id', tim],
    queryFn: () => api.errorsByRequestId(tim),
    enabled: tim.length > 0,
  });

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        label="Tra theo mã lượt gọi"
        placeholder="Dán mã người dùng đọc trên màn hình báo lỗi"
        value={requestId}
        onChange={(e) => setRequestId(e.target.value)}
        sx={{ mb: 2, maxWidth: 460 }}
      />

      {tim.length > 0 ? (
        byId.isLoading ? (
          <Skeleton variant="rounded" height={160} />
        ) : byId.data?.length ? (
          <Stack spacing={1.5}>
            {byId.data.map((e) => (
              <Paper key={e.id} variant="outlined" sx={{ p: 1.8 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{e.message}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {formatDateTime(e.createdAt)}
                  </Typography>
                </Stack>
                <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary', mt: 0.4 }}>
                  {e.source} · {e.path}
                </Typography>
                {e.stack && (
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.2,
                      bgcolor: tokens.line2,
                      borderRadius: '8px',
                      fontSize: 11.5,
                      overflow: 'auto',
                      maxHeight: 260,
                    }}
                  >
                    {e.stack}
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        ) : (
          <Note>Không có lỗi nào mang mã này. Kiểm lại xem đã dán đúng chưa.</Note>
        )
      ) : groups.isLoading ? (
        <Skeleton variant="rounded" height={240} />
      ) : groups.data?.length ? (
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>Thông điệp lỗi</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>
                  Số lần
                </TableCell>
                <TableCell sx={{ width: 160 }}>Gần nhất</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.data.map((g) => (
                <TableRow key={g.message} sx={{ '&:nth-of-type(even) td': { bgcolor: tokens.rowAlt } }}>
                  <TableCell sx={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }} title={g.message}>
                    {g.message}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: MONO, fontSize: 13 }}>
                    {g.count}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{formatDateTime(g.lastSeenAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Note>Chưa ghi nhận lỗi nào. Giữ 90 ngày rồi tự dọn.</Note>
      )}
    </Box>
  );
}

export function OpsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'metrics' | 'errors'>('metrics');

  if (user?.role !== 'WEB_ADMINISTRATOR') {
    return <Note tone="bad">Chỉ Web Administrator xem được màn vận hành.</Note>;
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}>
        VẬN HÀNH
      </Typography>
      <Typography variant="h5" sx={{ fontSize: 28, color: tokens.ink, mt: 0.5, mb: 2.5 }}>
        Hệ thống có đang khoẻ không
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 } }}>
        <Tabs
          value={tab}
          onChange={(_, v: 'metrics' | 'errors') => setTab(v)}
          sx={{ borderBottom: `1px solid ${tokens.line}`, minHeight: 40, mb: 2.5 }}
        >
          <Tab value="metrics" label="Số liệu" sx={{ fontSize: 13.5, minHeight: 40, textTransform: 'none' }} />
          <Tab value="errors" label="Lỗi" sx={{ fontSize: 13.5, minHeight: 40, textTransform: 'none' }} />
        </Tabs>

        {tab === 'metrics' ? <MetricsTab /> : <ErrorsTab />}
      </Paper>
    </Box>
  );
}
